import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "@phosphor-icons/react";

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-brand-night texture-grain flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <img src="/mayfair-logo.webp" alt="Mayfair Medical Services" className="h-20 w-auto object-contain rounded-sm mx-auto" />
          <h1 className="mt-6 font-serif text-3xl text-brand-bone">Admin Login</h1>
          <p className="mt-2 text-sm text-brand-bone/50">Mayfair Medical Services</p>
        </div>
        <form onSubmit={onSubmit} className="bg-brand-deep/60 border border-brand-bone/10 p-8 space-y-6 backdrop-blur" data-testid="admin-login-form">
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 px-4 py-3 rounded-sm">{error}</p>
          )}
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-brand-bone/70">Email</Label>
            <Input id="admin-email" data-testid="admin-email-input" type="email" required autoComplete="email"
              value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="bg-brand-night/60 border-brand-bone/15 text-brand-bone h-12 focus-visible:ring-brand-gold" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-brand-bone/70">Password</Label>
            <Input id="admin-password" data-testid="admin-password-input" type="password" required autoComplete="current-password"
              value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="bg-brand-night/60 border-brand-bone/15 text-brand-bone h-12 focus-visible:ring-brand-gold" />
          </div>
          <button type="submit" data-testid="admin-login-submit" disabled={loading}
            className="w-full rounded-full bg-brand-gold py-3.5 text-sm font-semibold text-brand-night transition-all duration-300 hover:bg-brand-goldlight disabled:opacity-60">
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
        <Link to="/" data-testid="admin-back-link" className="mt-6 flex items-center justify-center gap-2 text-sm text-brand-bone/40 hover:text-brand-gold transition-colors">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </motion.div>
    </div>
  );
}
